import React, { useState, useEffect } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { contactEntity } from '../../config/entities/contacts';
import { crmService } from '../../services/crmCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { ViewModal } from '../../components/common/ViewModal';
import { EditModal } from '../../components/common/EditModal';

export const ContactsPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdContact, setCreatedContact] = useState<any>(null);
  
  // Total count state
  const [totalContacts, setTotalContacts] = useState<number>(0);
  
  // Load total contacts count
  useEffect(() => {
    const loadTotalCount = async () => {
      try {
        // Use a very high limit to ensure we get all records and the correct total
        const response = await crmService.getContacts({ limit: 99999 });
        if (response && response.pagination && response.pagination.total) {
          setTotalContacts(response.pagination.total);
        } else if (response && typeof response === 'object' && 'total' in response) {
          setTotalContacts(response.total);
        } else if (Array.isArray(response)) {
          setTotalContacts(response.length);
        }
      } catch (error) {
        console.error('Error loading total contacts count:', error);
      }
    };
    
    loadTotalCount();
  }, []);

  // Use Dedicated CRM Service (same as dashboard) with wrapper for FlexibleCRUDTable compatibility
  const contactService = {
    getAll: (params: any) => crmService.getContacts(params),
    get: (id: string) => crmService.getContactById(id),
    create: (data: any) => crmService.createContact(data),
    update: (id: string, data: any) => crmService.updateContact(id, data),
    delete: (id: string) => crmService.deleteContact(id),
    getInteractions: (filters: any) => crmService.getInteractions(filters)
  };

  // CRUD handlers
  const handleViewContact = (contact: any) => {
    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const handleEditContact = (contact: any) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  };

  const handleDeleteContact = async (contact: any) => {
    const isDaveWagner = (contact.first_name === 'Dave' || contact.first_name === 'dave') && 
                         (contact.last_name === 'Wagner' || contact.last_name === 'wagner') &&
                         contact.email === 'dave@middlegroundcapital.com';
    
    if (isDaveWagner) {
      // Protected contact - no pre-check action
      void 0;
    }

    // Note: FlexibleCRUDTable will handle the confirmation dialog
    // This function just needs to perform the delete operation
    try {
      if (isDaveWagner) {
        // Protected contact - skip delete
        void 0;
      }

      await contactService.delete(contact.id);
      setTotalContacts(prev => Math.max(0, prev - 1));

      if (isDaveWagner) {
        // No post-delete action
        void 0;
      }
    } catch (error) {
      if (isDaveWagner) {
        console.error('[DELETE DEBUG] Error in handleDeleteContact:', error);
      }
      throw error; // Re-throw so FlexibleCRUDTable can handle it
    }
  };

  const handleCreateContact = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditSuccess = async (data: any) => {
    try {
      await contactService.update(selectedContact.id, data);
      setIsEditModalOpen(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleCreateSuccess = async (contact: any) => {
    try {
      const created = await contactService.create(contact);
      setCreatedContact(created);
      setTotalContacts(prev => prev + 1);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full py-6">
        {/* Total Count Display */}
        <div className="px-4 py-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Contacts
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {totalContacts}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 py-6">
          <FlexibleCRUDTable
            entity={contactEntity}
            service={contactService}
            onView={handleViewContact}
            onEdit={handleEditContact}
            onDelete={handleDeleteContact}
            onCreate={handleCreateContact}
            externalCreatedItem={createdContact}
            onOperationComplete={() => {
              // Table will automatically refresh after operations
            }}
            searchPlaceholder={t('crm.contacts.searchPlaceholder')}
            showPagination={true}
            showSearch={true}
            pageSize={10}
          />
        </div>
      </div>

      {/* View Contact Modal */}
      {isViewModalOpen && selectedContact && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedContact(null);
          }}
          title={t('crm.contacts.viewTitle', { entity: contactEntity.displayNameSingular || 'Contact' })}
          data={selectedContact}
          fields={contactEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}

      {/* Edit Contact Modal */}
      {isEditModalOpen && selectedContact && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedContact(null);
          }}
          title={t('crm.contacts.editTitle', { entity: contactEntity.displayNameSingular || 'Contact' })}
          entity={contactEntity}
          initialData={selectedContact}
          onSubmit={handleEditSuccess}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedContact(null);
          }}
          loading={false}
        />
      )}

      {/* Create Contact Modal */}
      {isCreateModalOpen && (
        <EditModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title={t('crm.contacts.createTitle', { entity: contactEntity.displayNameSingular || 'Contact' })}
          entity={contactEntity}
          onSubmit={handleCreateSuccess}
          onCancel={() => setIsCreateModalOpen(false)}
          loading={false}
        />
      )}

    </div>
  );
};
